/**
 * The drawing surface the renderer needs: a structural subset of the browser's
 * CanvasRenderingContext2D, so the same code draws in a browser canvas, a
 * recording context in tests, and (Phase 3) a PDF-operator writer.
 *
 * The renderer always draws in **PDF page space, y up** — exactly like
 * CoreGraphics in TakeoffDrawingAnnotation. The caller sets the transform
 * (pdf.js `viewport.transform` does page space → canvas, including /Rotate).
 */
export interface Ctx {
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(radians: number): void;
  scale(x: number, y: number): void;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, r: number, start: number, end: number, counterclockwise?: boolean): void;
  ellipse(x: number, y: number, rx: number, ry: number, rotation: number, start: number, end: number, counterclockwise?: boolean): void;
  rect(x: number, y: number, w: number, h: number): void;
  fill(fillRule?: "nonzero" | "evenodd"): void;
  stroke(): void;
  clip(fillRule?: "nonzero" | "evenodd"): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  setLineDash(segments: number[]): void;
  drawImage(image: unknown, dx: number, dy: number, dw: number, dh: number): void;
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineCap: string;
  lineJoin: string;
  font: string;
  globalAlpha: number;
  globalCompositeOperation: string;
}

/** Where images (logos, signature ink, tool-chest pictures) come from. */
export interface RenderEnv {
  /** A decoded image for base64 PNG/JPEG data, or null while it is still loading. */
  image?(base64: string): { image: unknown; width: number; height: number } | null;
  /** Date text for the classic signature block ("Oct 9, 2026 at 2:47 PM"). */
  formatTimestamp?(iso: string): string;
}

export interface Rect { x: number; y: number; w: number; h: number }
export const rect = (x: number, y: number, w: number, h: number): Rect => ({ x, y, w, h });
export const minX = (r: Rect) => r.x;
export const minY = (r: Rect) => r.y;
export const maxX = (r: Rect) => r.x + r.w;
export const maxY = (r: Rect) => r.y + r.h;
export const midX = (r: Rect) => r.x + r.w / 2;
export const midY = (r: Rect) => r.y + r.h / 2;
/** CGRect.insetBy (a rect inset past zero collapses to its centre, like CG's null rect). */
export function inset(r: Rect, dx: number, dy: number): Rect {
  const w = r.w - 2 * dx, h = r.h - 2 * dy;
  if (w < 0 || h < 0) return { x: midX(r), y: midY(r), w: Math.max(0, w), h: Math.max(0, h) };
  return { x: r.x + dx, y: r.y + dy, w, h };
}
export const fromBox = ([x, y, w, h]: readonly [number, number, number, number]): Rect => ({ x, y, w, h });
